import React, { useState } from "react";
import { format } from "date-fns";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { formatTimeToAmPm } from "../../utils/DateUtil";
import Modal from "../../components/Modal";
import ChangeShiftModal from "./ChangeShiftModal";

const ROLE_ICONS: Record<string, string> = {
  doctor: "🩺",
  physicians: "🩺",
  nurse: "💉",
  nurses: "💉",
  manager: "📋",
  managers: "📋",
  admin: "📋",
  lab: "🔬",
  laboratory: "🔬",
  lab_technician: "🔬",
  lab_manager: "🔬",
  pharmacy: "💊",
  pharmacist: "💊",
};

const getRoleIcon = (roleName: string): string => {
  const key = roleName.toLowerCase().replace(/\s+/g, "_");
  return ROLE_ICONS[key] ?? "👤";
};

export interface OnDutyUser {
  assignmentId: number;
  shiftDefinitionId: number;
  clinicUserId: number;
  userId: number;
  name: string;
}

export interface OnDutyRoleGroup {
  role: { id: number; name: string };
  users: OnDutyUser[];
}

interface WhosOnDutyTodayProps {
  currentShift?: { name: string; startTime: string; endTime: string } | null;
  byRole?: OnDutyRoleGroup[];
  dateLabel?: string;
  loading?: boolean;
  onShiftChanged?: () => void;
}

const WhosOnDutyToday: React.FC<WhosOnDutyTodayProps> = ({
  currentShift,
  byRole = [],
  dateLabel,
  loading = false,
  onShiftChanged,
}) => {
  const today = dateLabel ?? format(new Date(), "EEEE, MMMM d, yyyy");
  const [changeShiftUser, setChangeShiftUser] = useState<OnDutyUser | null>(null);

  const shift = currentShift || {
    name: "All Shifts",
    startTime: "",
    endTime: "",
  };

  const hasData = byRole.length > 0;
  const roleGroups = hasData ? byRole : [];

  return (
    <div className="mb-10 bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 sm:px-6">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            Who&apos;s On Duty Today
          </h2>
          <div className="flex flex-wrap gap-3 items-center text-sm text-gray-600">
            <span>{today}</span>
            <span className="hidden sm:inline">•</span>
            <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-sm font-medium text-primary">
              {shift.name}
              {shift.startTime && shift.endTime
                ? ` (${formatTimeToAmPm(shift.startTime)} – ${formatTimeToAmPm(shift.endTime)})`
                : ""}
            </span>
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="flex justify-center items-center py-8 text-sm text-gray-500">
            Loading…
          </div>
        ) : roleGroups.length === 0 ? (
          <div className="py-8 text-sm text-center text-gray-500">
            No one on duty for the selected date and shift.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {roleGroups.map((group) => (
              <div
                key={group.role.id}
                className="p-4 rounded-lg border border-gray-200 bg-gray-50/50"
              >
                <div className="flex gap-2 items-center mb-3">
                  <span className="text-lg">{getRoleIcon(group.role.name)}</span>
                  <h3 className="text-sm font-semibold text-gray-900 capitalize">
                    {group.role.name}
                  </h3>
                </div>
                <ul className="space-y-1.5">
                  {group.users.map((user) => (
                    <li
                      key={user.assignmentId}
                      className="flex gap-2 items-center text-sm text-gray-700"
                    >
                      {onShiftChanged && (
                        <button
                          type="button"
                          onClick={() => setChangeShiftUser(user)}
                          className="shrink-0 p-0.5 text-gray-500 hover:text-primary"
                          title="Change shift"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      )}
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <span className="break-words min-w-0">{user.name || "—"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {changeShiftUser && (
        <Modal
          title="Change Shift"
          onClose={() => setChangeShiftUser(null)}
          modalContent={
            <ChangeShiftModal
              assignmentId={changeShiftUser.assignmentId}
              userName={changeShiftUser.name || "User"}
              currentShiftId={changeShiftUser.shiftDefinitionId}
              onSuccess={onShiftChanged ?? (() => { })}
              onClose={() => setChangeShiftUser(null)}
            />
          }
        />
      )}
    </div>
  );
};

export default WhosOnDutyToday;
